import './styles/globals.css';
import './i18n'; // Phase 7aa — initialise i18next as early as possible
/*
 * Side-effect import, and it must stay above createInertiaApp. The browser
 * fires `beforeinstallprompt` once and early — usually before React mounts —
 * so the listener has to be registered at module load. Register it any later
 * (say, inside the component that draws the Install button) and the event has
 * already come and gone, leaving the app permanently unable to install itself.
 */
import './lib/installPrompt';
import { createInertiaApp } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from '@/app/layouts/AppShell';
import { initTheme } from '@/lib/theme';
import { pageTitle } from '@/lib/brand';

/*
 * Apply the persisted theme BEFORE React mounts. If we waited for React
 * to run applyTheme() in an effect, premium users would see a flash of
 * the default light theme for one paint cycle. Doing it here — right
 * after the stylesheet import — means the very first paint the user
 * sees is already in their chosen theme.
 */
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

type PageModule = { default: React.ComponentType<unknown> };

void createInertiaApp({
  title: pageTitle,
  resolve: (name) => {
    const pages = import.meta.glob<PageModule>('./pages/**/*.tsx');
    const page = pages[`./pages/${name}.tsx`];
    if (!page) throw new Error(`Inertia page not found: ${name}`);
    return page();
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <QueryClientProvider client={queryClient}>
        <AppShell initialPage={props.initialPage}>
          <App {...props} />
        </AppShell>
      </QueryClientProvider>,
    );
    // Signal the pre-React splash overlay (rendered from Blade) that
    // React has mounted and it's safe to fade out. Two ticks so the
    // first paint has actually flushed to screen — otherwise the
    // splash disappears BEFORE the app is visible, briefly showing
    // the previous cached content on some Android browsers.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        (window as unknown as { __APP_READY__?: boolean }).__APP_READY__ = true;
      });
    });
  },
  // Phase 8.5 — Premium progress bar tuned for our brand indigo.
  // Smaller delay so feedback feels instantaneous on fast hops; thicker bar
  // so it reads on high-density displays; matches our primary color token.
  progress: {
    color: 'hsl(240 65% 50%)',
    delay: 100,           // ms before the bar appears (avoid flash on fast nav)
    includeCSS: true,
    showSpinner: false,   // top bar only — modern SaaS pattern
  },
});
